/**
 * `/l/:slug` frame: loads `GET /v1/l/:slug`, publishes it to the providers above the router (locale, timezone,
 * currency, calm), applies the lottery theme, records the arrival (§12.1) and scopes analytics to the slug.
 * States (§4.4): layout-matching skeleton → delay notice after 10 s; not-found and load-failure with a next
 * action; a stale-data label when a refetch fails but a cached payload exists (Appendix A `stale_data`).
 */
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, ErrorState, useIntl } from '@windrop/ui';
import { LinkButton } from '../components/LinkButton';
import { LotteryFooter } from '../components/LotteryFooter';
import { LotteryHeader } from '../components/LotteryHeader';
import { RouteLoading } from '../components/RouteLoading';
import { ApiError, getApi, queryKeys } from '../lib/api';
import { configureAnalytics } from '../lib/analytics';
import { captureArrival } from '../lib/arrival';
import { setActiveLottery } from '../lib/lottery-store';
import { STALE_KEY } from '../lib/offline';
import { useLotteryTheme } from '../lib/theme';
import { LotteryContext, type LotteryContextValue } from './lottery-context';

export function LotteryLayout() {
  const { slug = '' } = useParams();
  const { search } = useLocation();
  const { t, formatDateTime } = useIntl();
  const query = useQuery({
    queryKey: queryKeys.lottery(slug),
    queryFn: ({ signal }) => getApi().getLottery(slug, { signal }),
    enabled: slug.length > 0,
  });
  const lottery = query.data;
  const theme = useLotteryTheme(lottery);

  useEffect(() => {
    if (!lottery) return;
    setActiveLottery(lottery);
    return () => setActiveLottery(null);
  }, [lottery]);

  useEffect(() => {
    if (!slug) return;
    // Router location (not window.location) so memory routers and in-app navigations capture the same way.
    const arrival = captureArrival(
      slug,
      { search },
      typeof document !== 'undefined' ? document.referrer : '',
    );
    configureAnalytics({ lotterySlug: slug, campaign: arrival.campaign });
  }, [slug, search]);

  const { refetch, isFetching, isError, dataUpdatedAt } = query;
  const value = useMemo<LotteryContextValue | null>(
    () =>
      lottery
        ? {
            slug,
            lottery,
            theme,
            refetch: () => refetch(),
            refreshing: isFetching,
            staleSince: isError && dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null,
          }
        : null,
    [lottery, theme, slug, refetch, isFetching, isError, dataUpdatedAt],
  );

  if (!lottery) {
    if (query.isPending || query.isFetching) {
      return (
        <RouteLoading
          shape="lottery"
          label={t('shell.loadingLottery', {}, 'Loading the lottery')}
          onRetry={() => void query.refetch()}
        />
      );
    }
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <main id="main" className="page page--narrow">
        <ErrorState
          title={notFound ? t('lottery.notFound.title') : t('lottery.loadFailed.title')}
          body={notFound ? t('lottery.notFound.body') : t('lottery.loadFailed.body')}
          reference={query.error instanceof ApiError ? query.error.problem.supportReference : undefined}
          action={
            notFound ? (
              <LinkButton to="/support" variant="primary">
                {t('shell.support', {}, 'Help and support')}
              </LinkButton>
            ) : (
              <Button variant="primary" onClick={() => void query.refetch()} loading={query.isFetching}>
                {t('shell.retry', {}, 'Retry')}
              </Button>
            )
          }
          secondaryAction={
            notFound ? null : (
              <LinkButton to="/support" variant="secondary">
                {t('shell.support', {}, 'Help and support')}
              </LinkButton>
            )
          }
        />
      </main>
    );
  }

  return (
    <LotteryContext.Provider value={value}>
      <div className="lottery-frame" data-lottery={lottery.slug} data-status={lottery.status}>
        <LotteryHeader lottery={lottery} />
        {value?.staleSince ? (
          <p className="stale-label" role="status" data-testid="stale-label">
            {t(STALE_KEY, { asOf: formatDateTime(value.staleSince) })}
          </p>
        ) : null}
        <Outlet />
        <LotteryFooter lottery={lottery} />
      </div>
    </LotteryContext.Provider>
  );
}
